import { useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { Phone, Download, XCircle, Loader2, Edit2, Trash2, Lock, Save, Coins, CheckCircle2, Search } from "lucide-react";

function getBookingTypeBadge(type) {
  return type === "ride" ? { text: "Covoiturage", cls: "bg-[#0A2540]/10 text-[#0A2540]" } : { text: "Location", cls: "bg-[#FACC15]/20 text-[#0A2540]" };
}

function getStatusBadge(status) {
  const map = {
    confirmed: { text: "Confirme", cls: "bg-[#10B981]/10 text-[#10B981]" },
    in_progress: { text: "En cours", cls: "bg-[#0A2540]/10 text-[#0A2540]" },
    pending: { text: "En attente", cls: "bg-[#FACC15]/20 text-[#EAB308]" },
    completed: { text: "Termine", cls: "bg-[#10B981]/10 text-[#10B981]" },
  };
  return map[status] || { text: status, cls: "bg-[#EF4444]/10 text-[#EF4444]" };
}

export function BookingFilters({ filter, setFilter, search, setSearch, onExport }) {
  return (
    <div className="flex flex-col sm:flex-row gap-3 mb-4">
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-[#94A3B8] pointer-events-none" />
        <Input
          placeholder="Rechercher client, chauffeur, téléphone, réf..."
          value={search || ""}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8 h-9 border-[#CBD5E1]"
          data-testid="booking-search-input"
        />
      </div>
      <Select value={filter} onValueChange={setFilter}>
        <SelectTrigger className="w-48 h-9 border-[#CBD5E1]" data-testid="booking-status-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tous les statuts</SelectItem>
          <SelectItem value="pending">En attente</SelectItem>
          <SelectItem value="confirmed">Confirme</SelectItem>
          <SelectItem value="in_progress">En cours</SelectItem>
          <SelectItem value="completed">Termine</SelectItem>
          <SelectItem value="cancelled">Annule</SelectItem>
        </SelectContent>
      </Select>
      <Button size="sm" variant="outline" onClick={onExport} className="border-[#CBD5E1] h-9" data-testid="export-bookings-csv">
        <Download className="w-3 h-3 mr-1" /> Export CSV
      </Button>
    </div>
  );
}

export function BookingsList({ bookings, onRefresh }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.is_super_admin === true;
  const [cancellingId, setCancellingId] = useState(null);
  const [confirmCancelId, setConfirmCancelId] = useState(null);
  const [refundingId, setRefundingId] = useState(null);
  const [confirmRefundBooking, setConfirmRefundBooking] = useState(null);

  const cancelBooking = async (bookingId) => {
    setCancellingId(bookingId);
    setConfirmCancelId(null);
    try {
      await api.post(`/bookings/${bookingId}/cancel`);
      toast.success("Réservation annulée");
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setCancellingId(null); }
  };

  const refundTft = async (bookingId) => {
    setRefundingId(bookingId);
    setConfirmRefundBooking(null);
    try {
      const { data } = await api.post(`/admin/bookings/${bookingId}/refund-tft`);
      toast.success(data.message || "TFT remboursé");
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setRefundingId(null); }
  };

  if (bookings.length === 0) return <p className="text-center py-10 text-[#64748B]">Aucune reservation</p>;

  return (
    <div className="grid gap-4">
      {bookings.map((b) => {
        const typeBadge = getBookingTypeBadge(b.booking_type);
        const statusBadge = getStatusBadge(b.status);
        const payBadge = b.payment_status === "paid"
          ? { text: "Paye", cls: "bg-[#10B981]/10 text-[#10B981]" }
          : b.payment_status === "reserved"
          ? { text: "Reserve", cls: "bg-[#3B82F6]/10 text-[#3B82F6]" }
          : { text: "Non paye", cls: "bg-[#EF4444]/10 text-[#EF4444]" };
        return (
          <div key={b.id} className="bg-white rounded-xl border border-[#CBD5E1] p-4" data-testid={`admin-booking-${b.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeBadge.cls}`}>{typeBadge.text}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge.cls}`}>{statusBadge.text}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${payBadge.cls}`}>{payBadge.text}</span>
                </div>
                <p className="text-sm text-[#0A2540]">
                  Client: {b.user_name} ({b.user_phone || "N/A"}) | Chauffeur: {b.driver_name || "N/A"}
                </p>
                <p className="text-xs text-[#64748B]">{b.total_amount?.toLocaleString()} XAF | {b.created_at?.split("T")[0]}</p>
                {b.booking_type === "rental" && (b.start_date || b.end_date) && (
                  <p className="text-[11px] text-[#0A2540]" data-testid={`admin-booking-dates-${b.id}`}>
                    <strong>Réservation :</strong> {b.start_date || "?"}{b.end_date && b.end_date !== b.start_date ? ` → ${b.end_date}` : ""}
                  </p>
                )}
                {b.booking_type === "rental" && (b.start_time || b.end_time) && (
                  <p className="text-[11px] text-[#0A2540] mt-0.5" data-testid={`admin-booking-times-${b.id}`}>
                    {b.start_time && <><strong>Départ :</strong> {b.start_time}</>}
                    {b.start_time && b.end_time && " · "}
                    {b.end_time && <><strong>Retour :</strong> {b.end_time}</>}
                  </p>
                )}
                {b.status === "cancelled" && b.cancellation_reason_label && (
                  <div className="mt-2 rounded-md bg-[#FEF3C7] border border-[#FACC15]/40 px-2.5 py-1.5" data-testid={`admin-cancel-reason-${b.id}`}>
                    <p className="text-[11px] text-[#92400E] leading-snug">
                      <strong>Motif annulation ({b.cancelled_by_role || "?"}) :</strong> {b.cancellation_reason_label}
                    </p>
                    {b.cancellation_reason_details && (
                      <p className="text-[11px] italic text-[#7C2D12] mt-0.5">« {b.cancellation_reason_details} »</p>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {b.status !== "completed" && b.status !== "cancelled" && (
                  isSuperAdmin ? (
                    <Button size="sm" variant="outline" onClick={() => setConfirmCancelId(b.id)} disabled={cancellingId === b.id}
                      className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10" data-testid={`admin-cancel-booking-${b.id}`}>
                      {cancellingId === b.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <XCircle className="w-3.5 h-3.5 mr-1" />}
                      Annuler
                    </Button>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] text-[#94A3B8]" title="Action réservée au Super Admin">
                      <Lock className="w-3 h-3" /> Super Admin
                    </span>
                  )
                )}
                {b.status === "cancelled" && b.tft_debited && !b.tft_refunded && (
                  isSuperAdmin ? (
                    <Button size="sm" variant="outline" onClick={() => setConfirmRefundBooking(b)} disabled={refundingId === b.id}
                      className="border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10" data-testid={`admin-refund-tft-${b.id}`}>
                      {refundingId === b.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Coins className="w-3.5 h-3.5 mr-1" />}
                      Rembourser TFT
                    </Button>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] text-[#94A3B8]" title="Action réservée au Super Admin">
                      <Lock className="w-3 h-3" /> Super Admin
                    </span>
                  )
                )}
                {b.status === "cancelled" && b.tft_refunded && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-[#10B981] font-medium" data-testid={`tft-refunded-badge-${b.id}`}>
                    <CheckCircle2 className="w-3 h-3" /> TFT remboursé
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {/* Cancel confirm dialog */}
      <Dialog open={!!confirmCancelId} onOpenChange={(o) => !o && setConfirmCancelId(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="confirm-cancel-booking-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#EF4444]" style={{ fontFamily: 'Outfit' }}>Confirmer l&apos;annulation</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">
              La réservation et son paiement seront marqués comme annulés. La commission TFT reste débitée — vous pourrez la rembourser manuellement depuis cette même liste.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setConfirmCancelId(null)} className="flex-1 border-[#CBD5E1]" data-testid="cancel-cancel-btn">Non, garder</Button>
            <Button onClick={() => cancelBooking(confirmCancelId)} className="flex-1 bg-[#EF4444] text-white hover:bg-[#DC2626]" data-testid="confirm-cancel-btn">
              <XCircle className="w-3.5 h-3.5 mr-1" /> Oui, annuler
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Refund TFT confirm dialog */}
      <Dialog open={!!confirmRefundBooking} onOpenChange={(o) => !o && setConfirmRefundBooking(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="confirm-refund-tft-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#10B981] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Coins className="w-5 h-5" /> Rembourser la commission TFT
            </DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">
              Le chauffeur <strong className="text-[#0A2540]">{confirmRefundBooking?.driver_name || "—"}</strong> recevra{" "}
              <strong className="text-[#10B981]">+{(confirmRefundBooking?.tft_debit_amount || 0).toLocaleString()} TFT</strong>{" "}
              sur son compte. Cette action est <u>irréversible</u> et idempotente (un seul remboursement par réservation).
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setConfirmRefundBooking(null)} className="flex-1 border-[#CBD5E1]" data-testid="cancel-refund-tft-btn">Annuler</Button>
            <Button onClick={() => refundTft(confirmRefundBooking?.id)} className="flex-1 bg-[#10B981] text-white hover:bg-[#059669]" data-testid="confirm-refund-tft-btn">
              <Coins className="w-3.5 h-3.5 mr-1" /> Rembourser
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function PaymentFilters({ filter, setFilter, search, setSearch, onExport }) {
  return (
    <div className="flex flex-col sm:flex-row gap-3 mb-4">
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-[#94A3B8] pointer-events-none" />
        <Input
          placeholder="Rechercher référence, téléphone, montant..."
          value={search || ""}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8 h-9 border-[#CBD5E1]"
          data-testid="payment-search-input"
        />
      </div>
      <Select value={filter} onValueChange={setFilter}>
        <SelectTrigger className="w-48 h-9 border-[#CBD5E1]" data-testid="payment-method-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Toutes les methodes</SelectItem>
          <SelectItem value="mtn_momo">MTN MoMo</SelectItem>
          <SelectItem value="orange_money">Orange Money</SelectItem>
          <SelectItem value="cash">Cash</SelectItem>
        </SelectContent>
      </Select>
      <Button size="sm" variant="outline" onClick={onExport} className="border-[#CBD5E1] h-9" data-testid="export-payments-csv">
        <Download className="w-3 h-3 mr-1" /> Export CSV
      </Button>
    </div>
  );
}

function getPaymentMethodBadge(method) {
  if (method === "mtn_momo") return { text: "MTN MoMo", cls: "bg-[#FFC107]/20 text-[#0A2540]" };
  if (method === "orange_money") return { text: "Orange Money", cls: "bg-[#FF6600]/20 text-[#FF6600]" };
  return { text: "Cash", cls: "bg-[#10B981]/10 text-[#10B981]" };
}

export function PaymentsTable({ payments, onRefresh }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.is_super_admin === true;
  const [editing, setEditing] = useState(null); // payment object
  const [deleting, setDeleting] = useState(null); // payment object
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ amount: "", method: "cash", status: "", transaction_ref: "", phone_number: "" });

  const startEdit = (p) => {
    setForm({
      amount: p.amount ?? "",
      method: p.method || "cash",
      status: p.status || "reserved",
      transaction_ref: p.transaction_ref || "",
      phone_number: p.phone_number || "",
    });
    setEditing(p);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.patch(`/admin/payments/${editing.id}`, {
        amount: Number(form.amount) || 0,
        method: form.method,
        status: form.status,
        transaction_ref: form.transaction_ref,
        phone_number: form.phone_number,
      });
      toast.success("Paiement modifié");
      setEditing(null);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setSaving(true);
    try {
      await api.delete(`/admin/payments/${deleting.id}`);
      toast.success("Paiement supprimé");
      setDeleting(null);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-[#CBD5E1] overflow-hidden">
      {!isSuperAdmin && (
        <div className="px-3 py-2 bg-[#FEF3C7] text-[#92400E] text-[11px] flex items-center gap-2 border-b border-[#CBD5E1]" data-testid="payments-superadmin-notice">
          <Lock className="w-3.5 h-3.5" />
          La modification et la suppression des paiements sont réservées au <strong>Super Admin</strong>.
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#CBD5E1] bg-[#F8FAFC]">
              <th className="text-left text-xs font-semibold text-[#64748B] p-3">Reference</th>
              <th className="text-left text-xs font-semibold text-[#64748B] p-3">Montant</th>
              <th className="text-left text-xs font-semibold text-[#64748B] p-3">Methode</th>
              <th className="text-left text-xs font-semibold text-[#64748B] p-3">Statut</th>
              <th className="text-left text-xs font-semibold text-[#64748B] p-3">Date</th>
              <th className="text-right text-xs font-semibold text-[#64748B] p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => {
              const methodBadge = getPaymentMethodBadge(p.method);
              return (
                <tr key={p.id} className="border-b border-[#CBD5E1] last:border-0" data-testid={`admin-payment-${p.id}`}>
                  <td className="p-3 text-sm font-mono text-[#0A2540]">{p.transaction_ref}</td>
                  <td className="p-3 text-sm font-bold text-[#0A2540]">{p.amount?.toLocaleString()} XAF</td>
                  <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${methodBadge.cls}`}>{methodBadge.text}</span></td>
                  <td className="p-3"><span className="text-xs text-[#10B981] font-medium">{p.status}</span></td>
                  <td className="p-3 text-xs text-[#64748B]">{p.created_at?.split("T")[0]}</td>
                  <td className="p-3 text-right">
                    {isSuperAdmin ? (
                      <div className="inline-flex items-center gap-1">
                        <Button size="sm" variant="outline" onClick={() => startEdit(p)} className="h-7 px-2 border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540]/10" data-testid={`edit-payment-${p.id}`}>
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setDeleting(p)} className="h-7 px-2 border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10" data-testid={`delete-payment-${p.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] text-[#94A3B8]" title="Action réservée au Super Admin">
                        <Lock className="w-3 h-3" /> Verrouillé
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md" data-testid="edit-payment-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Modifier le paiement</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">Action réservée au Super Admin. Soyez prudent.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-[#0A2540] text-xs">Référence</Label>
                <Input value={form.transaction_ref} onChange={(e) => setForm({ ...form, transaction_ref: e.target.value })} className="h-9 border-[#CBD5E1] font-mono" data-testid="edit-payment-ref" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#0A2540] text-xs">Montant (XAF)</Label>
                  <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-9 border-[#CBD5E1]" data-testid="edit-payment-amount" />
                </div>
                <div>
                  <Label className="text-[#0A2540] text-xs">Méthode</Label>
                  <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                    <SelectTrigger className="h-9 border-[#CBD5E1]" data-testid="edit-payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="mtn_momo">MTN MoMo</SelectItem>
                      <SelectItem value="orange_money">Orange Money</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#0A2540] text-xs">Statut</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="h-9 border-[#CBD5E1]" data-testid="edit-payment-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reserved">Réservé</SelectItem>
                      <SelectItem value="paid">Payé</SelectItem>
                      <SelectItem value="refunded">Remboursé</SelectItem>
                      <SelectItem value="cancelled">Annulé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#0A2540] text-xs">Téléphone</Label>
                  <Input value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} className="h-9 border-[#CBD5E1]" data-testid="edit-payment-phone" />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(null)} className="flex-1 border-[#CBD5E1]" data-testid="cancel-edit-payment">Annuler</Button>
                <Button onClick={saveEdit} disabled={saving} className="flex-1 bg-[#0A2540] text-white hover:bg-[#081b2e]" data-testid="confirm-edit-payment">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-payment-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#EF4444]" style={{ fontFamily: 'Outfit' }}>Supprimer le paiement</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">
              Cette suppression est définitive. Référence: <span className="font-mono">{deleting?.transaction_ref}</span> – {deleting?.amount?.toLocaleString()} XAF.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setDeleting(null)} className="flex-1 border-[#CBD5E1]" data-testid="cancel-delete-payment">Annuler</Button>
            <Button onClick={confirmDelete} disabled={saving} className="flex-1 bg-[#EF4444] text-white hover:bg-[#DC2626]" data-testid="confirm-delete-payment">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />} Supprimer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
